import { Checkbox } from '@mui/material';
import { AllocationRule } from 'further-types/lp/allocations';
import { DrawdownSourcePriority } from 'further-types/lp/capital-action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { round } from 'lodash';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { UpdateCapitalActionFormValues } from '../../../hooks/useCapitalActionForm';
import useAllocateCapitalCall from './hooks/useAllocateCapitalCall';
import useAllocationChangeHandler from './hooks/useAllocationChangeHandler';
import useRecallableDistributionCalledInOtherEntries from './hooks/useRecallableDistributionCalledInOtherEntries';

const getDrawdownSourcePriorities = (showUncalled: boolean) => [
  {
    value: DrawdownSourcePriority.UnallocatedCommitmentFirst,
    label: showUncalled
      ? `From uncalled commitment first`
      : `From unallocated commitment first`,
  },
  {
    value: DrawdownSourcePriority.RecallableDistributionFirst,
    label: `From recallable distribution first`,
  },
  {
    value: DrawdownSourcePriority.Manual,
    label: `Manual allocation`,
  },
];

type Props = {
  entryIndex: number;
  calledFundsLabel: string;
  currency: CurrencyCode;
  allocationRule: AllocationRule;
  capitalCalledLcy: number;
  vehicleId: string;
  actionId?: string;
  showUncalledCommitment?: boolean;
  onChange: (
    capitalBreakdown: Array<{
      commitmentId: string;
      fundsCalled?: {
        fromCommitmentLcy: number;
        fromRecallableDistributionLcy: number;
      };
    }>,
  ) => void;
  capitalBreakdown: Array<{
    commitmentId: string;
    fundsCalled?: {
      fromCommitmentLcy: number;
      fromRecallableDistributionLcy: number;
    };
  }>;
  keepCallAmountFixed: boolean;
};

const CapitalCallAllocations: React.FC<Props> = ({
  currency,
  allocationRule,
  capitalCalledLcy,
  vehicleId,
  actionId,
  entryIndex,
  onChange,
  capitalBreakdown,
  calledFundsLabel,
  keepCallAmountFixed,
  showUncalledCommitment = false,
}) => {
  const [excludedCommitmentIds, setExcludedCommitmentIds] = useState<
    Array<string>
  >(
    capitalBreakdown
      .filter(
        (cb) =>
          round(
            (cb.fundsCalled?.fromCommitmentLcy ?? 0) +
              (cb.fundsCalled?.fromRecallableDistributionLcy ?? 0),
            2,
          ) === 0,
      )
      .map((c) => c.commitmentId),
  );

  const { control, watch, setValue } =
    useFormContext<UpdateCapitalActionFormValues>();

  const drawdownSourcePriority =
    watch(`entries.${entryIndex}.drawdownSourcePriority`) ??
    DrawdownSourcePriority.UnallocatedCommitmentFirst;

  const recallableDistributionCalledInOtherEntries =
    useRecallableDistributionCalledInOtherEntries(entryIndex);

  const {
    hasRecallableDistribution,
    isPending,
    calledCapitalAllocations,
    allocateCapitalCall,
  } = useAllocateCapitalCall({
    vehicleId,
    actionId,
    capitalCalledLcy,
    excludedCommitmentIds,
    drawdownSourcePriority,
    allocationRule,
    recallableDistributionCalledInOtherEntries,
    existingBreakdown: capitalBreakdown,
    onSuccess: onChange,
    isNewForm: !actionId && capitalBreakdown.length === 0,
    onFirstLoadExclusionsSync: (ids) =>
      setExcludedCommitmentIds((prev) => [
        ...prev,
        ...ids.filter((id) => !prev.includes(id)),
      ]),
  });

  const handleAllocationChange = useAllocationChangeHandler({
    capitalBreakdown,
    onAllocationsChange: onChange,
    keepCallAmountFixed,
  });

  const handleManualChange = () => {
    if (!hasRecallableDistribution) {
      return;
    }
    setValue(
      `entries.${entryIndex}.drawdownSourcePriority`,
      DrawdownSourcePriority.Manual,
    );
  };

  const handleDrawdownSourcePriorityChange = (
    drawdownSourcePriority: DrawdownSourcePriority,
  ) => {
    setValue(
      `entries.${entryIndex}.drawdownSourcePriority`,
      drawdownSourcePriority,
    );

    if (drawdownSourcePriority !== DrawdownSourcePriority.Manual) {
      allocateCapitalCall({
        useExistingBreakdown: false,
        drawdownSourcePriority,
      });
    }
  };

  const columns = [
    {
      label: 'Investor',
      key: 'investorLabel',
    },
    {
      label: 'Total commitment',
      key: 'totalCommitmentLcy',
    },
    {
      label: showUncalledCommitment
        ? 'Uncalled commitment'
        : 'Unallocated commitment',
      key: showUncalledCommitment
        ? 'uncalledCommitmentLcy'
        : 'unallocatedCommitmentLcy',
    },
    ...(hasRecallableDistribution
      ? [
          {
            label: 'Recallable distribution',
            key: 'recallableDistributionLcy',
          },
          {
            label: 'Total callable capital',
            key: 'availableToBeCalledLcy',
          },
        ]
      : []),
    {
      label: 'Close date',
      key: 'closeDate',
    },
    ...(hasRecallableDistribution
      ? [
          {
            label: calledFundsLabel,
            key: 'amountLcy',
            render: (row: { commitmentId: string }) => {
              const entry = capitalBreakdown.find(
                (c) => c.commitmentId === row.commitmentId,
              );
              return numberToCurrencyString(
                (entry?.fundsCalled?.fromCommitmentLcy ?? 0) +
                  (entry?.fundsCalled?.fromRecallableDistributionLcy ?? 0),
                { currency },
              );
            },
          },
        ]
      : []),

    {
      label: hasRecallableDistribution
        ? showUncalledCommitment
          ? 'From uncalled commitment'
          : 'From unallocated commitment'
        : calledFundsLabel,
      key: 'fromUnallocatedCommitmentLcy',
      render: (row: { commitmentId: string }) => {
        const capital = capitalBreakdown.find(
          (c) => c.commitmentId === row.commitmentId,
        );

        return (
          <NumberInput
            value={capital?.fundsCalled?.fromCommitmentLcy ?? 0}
            onChange={(value) => {
              handleAllocationChange(
                row.commitmentId,
                'fromCommitmentLcy',
                value ?? 0,
              );
              handleManualChange();
            }}
            decimals={2}
            fixedDecimalScale
            currency={currency}
            currencyStartAdornment
            placeholder="Enter amount"
            clearZeroValueOnFocus
            disabled={
              isPending || (keepCallAmountFixed && !hasRecallableDistribution)
            }
          />
        );
      },
    },
    ...(hasRecallableDistribution
      ? [
          {
            label: 'From recallable distribution',
            key: 'fromRecallableDistributionLcy',
            render: (row: { commitmentId: string }) => {
              const capital = capitalBreakdown.find(
                (c) => c.commitmentId === row.commitmentId,
              );
              return (
                <NumberInput
                  value={
                    capital?.fundsCalled?.fromRecallableDistributionLcy ?? 0
                  }
                  onChange={(value) => {
                    handleAllocationChange(
                      row.commitmentId,
                      'fromRecallableDistributionLcy',
                      value ?? 0,
                    );
                    handleManualChange();
                  }}
                  decimals={2}
                  fixedDecimalScale
                  currency={currency}
                  placeholder="Enter amount"
                  clearZeroValueOnFocus
                  disabled={isPending}
                />
              );
            },
          },
        ]
      : []),

    {
      label: 'Exclude',
      key: 'excludeFromAllocation',
      render: (row: {
        commitmentId: string;
        excludeFromAllocation: boolean;
      }) => (
        <Checkbox
          disabled={isPending || !!actionId}
          checked={row.excludeFromAllocation}
          onChange={(e) => {
            const { checked } = e.target;
            setExcludedCommitmentIds((prev) =>
              checked
                ? [...prev, row.commitmentId]
                : prev.filter((id) => id !== row.commitmentId),
            );
          }}
        />
      ),
    },
  ];

  const rows = calledCapitalAllocations.map((allocation) => ({
    id: allocation.commitmentId,
    commitmentId: allocation.commitmentId,
    investorLabel: allocation.investorLabel,
    totalCommitmentLcy: numberToCurrencyString(allocation.totalCommitmentLcy, {
      currency,
    }),
    uncalledCommitmentLcy: numberToCurrencyString(
      allocation.uncalledCommitmentLcy,
      { currency },
    ),
    unallocatedCommitmentLcy: numberToCurrencyString(
      allocation.unallocatedCommitmentLcy,
      { currency },
    ),
    closeDate: allocation.closeDate.toReadable(),
    allocationPercentage: `${round(allocation.allocationPercentage, 2)}%`,
    recallableDistributionLcy: numberToCurrencyString(
      allocation.recallableDistributionLcy,
      { currency },
    ),
    availableToBeCalledLcy: numberToCurrencyString(
      allocation.availableToBeCalledLcy,
      { currency },
    ),
    excludeFromAllocation: allocation.excludeFromAllocation,
  }));

  return (
    <Column spacing="md">
      {hasRecallableDistribution && (
        <FormField label="Call funds">
          <Controller
            name={`entries.${entryIndex}.drawdownSourcePriority`}
            control={control}
            render={({ fieldState: { error }, field }) => (
              <BasicSelect
                {...field}
                fullWidth
                placeholder="Select drawdown source priority"
                options={getDrawdownSourcePriorities(showUncalledCommitment)}
                disabled={isPending}
                error={!!error}
                onChange={handleDrawdownSourcePriorityChange}
              />
            )}
          />
        </FormField>
      )}
      <Table
        showHeaderBorder
        showTopBorder
        rows={rows}
        columns={columns}
        isLoading={isPending}
      />
    </Column>
  );
};

export default CapitalCallAllocations;
